import React, { useState, useEffect, useRef } from 'react';
import { X, CheckCircle, AlertCircle, Camera, Server, Wifi, Map } from 'lucide-react';
import LocationPickerModal from './LocationPickerModal';
import './DeviceConfigModal.css';

type DeviceType = 'camera' | 'xtamVideo' | 'miniot' | 'router' | 'button' | 'siren';

interface DeviceConfig {
  // Configuración de Cámara
  name?: string;
  siteAddress?: string;
  urlRtsp?: string;
  isOnvif?: boolean;
  onvifUrl?: string;
  onvifUsername?: string;
  onvifPassword?: string;
  latitude?: string;
  longitude?: string;
  xtamVideoChannel?: string;
  isFromDvr?: boolean;
  isAllowTesting?: boolean;
  isAllowAnalytics?: boolean;
  vmsId?: string;
  isMassive?: boolean;
  urlRtspOriginal?: string;
  recordingServerUrl?: string;
  /** FK a devices_sirens: bocina montada en el poste de esta cámara. null = ninguna. */
  sirenDeviceId?: number | null;

  // Configuración de XTAM Video
  state?: number;
  
  // Configuración de Router
  ipAddress?: string;
  
  // Configuración de Button
  identificationButton?: string;
  emailRegister?: string;
  cellphoneRegister?: number;
  idSubscriber?: number;
  role?: number;
  dataAuthorization?: boolean;
}

interface DeviceConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (config: DeviceConfig) => void;
  onCancel?: () => void;
  deviceType: DeviceType;
  deviceInfo: {
    id: number;
    serial: string;
    brand?: string;
    model?: string;
  };
  initialConfig?: DeviceConfig;
  isRequired?: boolean;
  siteServiceId?: number; // 1: PRIVADO, 2: EU, 3: PVVC
  /** Por defecto para cámara: lat/lng del sitio al que se asigna */
  siteLatitude?: string;
  siteLongitude?: string;
  /** Bocinas del sitio (normalmente 1). Habilita el check "tiene bocina" en cámaras. */
  siteSirens?: Array<{ id: number; ipaddress?: string | null }>;
}

const DeviceConfigModal: React.FC<DeviceConfigModalProps> = ({
  isOpen,
  onClose,
  onSave,
  onCancel,
  deviceType,
  deviceInfo,
  initialConfig,
  isRequired = false,
  siteServiceId,
  siteLatitude,
  siteLongitude,
  siteSirens,
}) => {
  const isPVVC = siteServiceId === 3;
  const [config, setConfig] = useState<DeviceConfig>({});
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const initializedRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      initializedRef.current = false;
      return;
    }
    if (initializedRef.current) return;
    initializedRef.current = true;

    const defaultLat = siteLatitude ?? '';
    const defaultLng = siteLongitude ?? '';
    if (deviceType === 'camera') {
      if (initialConfig) {
        setConfig({
          ...initialConfig,
          latitude: initialConfig.latitude ?? defaultLat,
          longitude: initialConfig.longitude ?? defaultLng,
        });
      } else {
        setConfig({ latitude: defaultLat, longitude: defaultLng });
      }
    } else if (initialConfig) {
      setConfig({ ...initialConfig });
    } else {
      setConfig({});
    }
    setErrors({});
  }, [isOpen, initialConfig, deviceType, siteLatitude, siteLongitude]);

  const getTitle = () => {
    const action = initialConfig ? 'Editar' : 'Configurar';
    switch (deviceType) {
      case 'camera': return `${action} Cámara`;
      case 'xtamVideo': return `${action} XTAM Video`;
      case 'miniot': return `${action} MinIoT`;
      case 'router': return `${action} Router`;
      case 'button': return `${action} Botón de Pánico`;
      case 'siren': return `${action} Sirena`;
      default: return `${action} Dispositivo`;
    }
  };

  const getIcon = () => {
    switch (deviceType) {
      case 'camera': return <Camera size={24} />;
      case 'xtamVideo': return <Server size={24} />;
      case 'miniot': return <Wifi size={24} />;
      case 'router': return <Wifi size={24} />;
      case 'button': return <AlertCircle size={24} />;
      case 'siren': return <AlertCircle size={24} />;
      default: return <AlertCircle size={24} />;
    }
  };

  const validateConfig = (): boolean => {
    const newErrors: {[key: string]: string} = {};

    if (deviceType === 'camera') {
      if (!config.name?.trim()) {
        newErrors.name = 'El nombre es requerido';
      }
      if (!config.urlRtsp?.trim()) {
        newErrors.urlRtsp = 'La URL RTSP es requerida';
      }
      if (config.isOnvif) {
        if (!config.onvifUrl?.trim()) {
          newErrors.onvifUrl = 'La URL ONVIF es requerida';
        }
        if (!config.onvifUsername?.trim()) {
          newErrors.onvifUsername = 'El usuario ONVIF es requerido';
        }
      }
      if (config.latitude != null && config.latitude.trim() !== '') {
        const lat = parseFloat(config.latitude);
        if (isNaN(lat) || lat < -90 || lat > 90) {
          newErrors.latitude = 'La latitud debe estar entre -90 y 90';
        }
      }
      if (config.longitude != null && config.longitude.trim() !== '') {
        const lng = parseFloat(config.longitude);
        if (isNaN(lng) || lng < -180 || lng > 180) {
          newErrors.longitude = 'La longitud debe estar entre -180 y 180';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validateConfig()) {
      onSave(config);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="device-config-overlay">
      <div className="device-config-container">
        {/* Header */}
        <div className="device-config-header">
          <div className="device-config-header-content">
            <div className="device-config-icon">
              {getIcon()}
            </div>
            <div>
              <h2 className="device-config-title">{getTitle()}</h2>
              <p className="device-config-subtitle">
                Serial: <span className="device-config-serial">{deviceInfo.serial}</span>
                {deviceInfo.brand && deviceInfo.model && (
                  <> • {deviceInfo.brand} {deviceInfo.model}</>
                )}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="device-config-close-btn"
            disabled={isRequired}
            hidden={isRequired}
          >
            <X size={24} />
          </button>
        </div>

        {/* Body */}
        <div className="device-config-body">
          {isRequired && (
            <div className="config-required-warning">
              <AlertCircle size={18} />
              <span>Es obligatorio configurar este dispositivo antes de continuar</span>
            </div>
          )}
          
          {/* Cámara */}
          {deviceType === 'camera' && (
            <div className="device-config-form">
              <div className="config-form-field">
                <label className="config-form-label">Nombre de la cámara *</label>
                <input
                  type="text"
                  value={config.name || ''}
                  onChange={(e) => setConfig({ ...config, name: e.target.value })}
                  className={`config-input ${errors.name ? 'input-error' : ''}`}
                  placeholder="Ej: Cámara Entrada Principal"
                />
                {errors.name && <span className="config-field-error">{errors.name}</span>}
              </div>

              <div className="config-form-field">
                <label className="config-form-label">Dirección de la cámara</label>
                <input
                  type="text"
                  value={config.siteAddress || ''}
                  onChange={(e) => setConfig({ ...config, siteAddress: e.target.value })}
                  className="config-input"
                  placeholder="Ej: Calle 100 #15-20, Bogotá"
                  maxLength={45}
                />
              </div>

              <div className="config-form-field">
                <label className="config-form-label">URL RTSP *</label>
                <input
                  type="text"
                  value={config.urlRtsp || ''}
                  onChange={(e) => setConfig({ ...config, urlRtsp: e.target.value })}
                  className={`config-input ${errors.urlRtsp ? 'input-error' : ''}`}
                  placeholder="rtsp://usuario:contraseña@192.168.1.100:554/stream"
                />
                {errors.urlRtsp && <span className="config-field-error">{errors.urlRtsp}</span>}
              </div>

              <div className="config-form-checkbox">
                <label className="config-checkbox-label">
                  <input
                    type="checkbox"
                    checked={config.isOnvif || false}
                    onChange={(e) => setConfig({ ...config, isOnvif: e.target.checked })}
                  />
                  <span>Soporta ONVIF</span>
                </label>
              </div>

              {config.isOnvif && (
                <>
                  <div className="config-form-field">
                    <label className="config-form-label">URL ONVIF *</label>
                    <input
                      type="text"
                      value={config.onvifUrl || ''}
                      onChange={(e) => setConfig({ ...config, onvifUrl: e.target.value })}
                      className={`config-input ${errors.onvifUrl ? 'input-error' : ''}`}
                      placeholder="http://192.168.1.100/onvif/device_service"
                    />
                    {errors.onvifUrl && <span className="config-field-error">{errors.onvifUrl}</span>}
                  </div>

                  <div className="config-form-row">
                    <div className="config-form-field">
                      <label className="config-form-label">Usuario ONVIF *</label>
                      <input
                        type="text"
                        value={config.onvifUsername || ''}
                        onChange={(e) => setConfig({ ...config, onvifUsername: e.target.value })}
                        className={`config-input ${errors.onvifUsername ? 'input-error' : ''}`}
                        placeholder="admin"
                      />
                      {errors.onvifUsername && <span className="config-field-error">{errors.onvifUsername}</span>}
                    </div>

                    <div className="config-form-field">
                      <label className="config-form-label">Contraseña ONVIF</label>
                      <input
                        type="password"
                        value={config.onvifPassword || ''}
                        onChange={(e) => setConfig({ ...config, onvifPassword: e.target.value })}
                        className="config-input"
                        placeholder="••••••••"
                      />
                    </div>
                  </div>
                </>
              )}

              <div className="config-form-field">
                <label className="config-form-label">Latitud</label>
                <input
                  type="text"
                  value={config.latitude ?? ''}
                  onChange={(e) => setConfig({ ...config, latitude: e.target.value })}
                  className={`config-input ${errors.latitude ? 'input-error' : ''}`}
                  placeholder="Ej: 4.7110"
                />
                {errors.latitude && <span className="config-field-error">{errors.latitude}</span>}
              </div>

              <div className="config-form-field">
                <label className="config-form-label">Longitud</label>
                <div className="config-input-with-button">
                  <input
                    type="text"
                    value={config.longitude ?? ''}
                    onChange={(e) => setConfig({ ...config, longitude: e.target.value })}
                    className={`config-input ${errors.longitude ? 'input-error' : ''}`}
                    placeholder="Ej: -74.0721"
                  />
                  <button
                    type="button"
                    onClick={() => setShowLocationPicker(true)}
                    className="config-location-btn"
                    title="Seleccionar en mapa"
                  >
                    <Map size={20} />
                  </button>
                </div>
                {errors.longitude && <span className="config-field-error">{errors.longitude}</span>}
              </div>

              <div className="config-form-row">
                {!isPVVC && (
                  <div className="config-form-field">
                    <label className="config-form-label">Canal XTAM Video</label>
                    <select
                      value={config.xtamVideoChannel || ''}
                      onChange={(e) => setConfig({ ...config, xtamVideoChannel: e.target.value })}
                      className="config-input"
                    >
                      <option value="">Seleccione un canal...</option>
                      <option value="camara1">camara1</option>
                      <option value="camara2">camara2</option>
                      <option value="camara3">camara3</option>
                      <option value="camara4">camara4</option>
                    </select>
                  </div>
                )}

                <div className="config-form-field">
                  <label className="config-form-label">VMS ID</label>
                  <input
                    type="text"
                    value={config.vmsId || ''}
                    onChange={(e) => setConfig({ ...config, vmsId: e.target.value })}
                    className="config-input"
                    placeholder="VMS-001"
                  />
                </div>
              </div>

              <div className="config-form-checkboxes">
                <label className="config-checkbox-label">
                  <input
                    type="checkbox"
                    checked={config.isFromDvr || false}
                    onChange={(e) => setConfig({ ...config, isFromDvr: e.target.checked })}
                  />
                  <span>Es desde DVR</span>
                </label>

                <label className="config-checkbox-label">
                  <input
                    type="checkbox"
                    checked={config.isAllowTesting !== false}
                    onChange={(e) => setConfig({ ...config, isAllowTesting: e.target.checked })}
                  />
                  <span>Permitir pruebas</span>
                </label>

                <label className="config-checkbox-label">
                  <input
                    type="checkbox"
                    checked={config.isAllowAnalytics || false}
                    onChange={(e) => setConfig({ ...config, isAllowAnalytics: e.target.checked })}
                  />
                  <span>Permitir analíticas</span>
                </label>

                <label className="config-checkbox-label">
                  <input
                    type="checkbox"
                    checked={config.isMassive || false}
                    onChange={(e) => setConfig({ ...config, isMassive: e.target.checked })}
                  />
                  <span>API Gateway (masiva)</span>
                </label>

                {(siteSirens?.length ?? 0) > 0 && (
                  <label
                    className="config-checkbox-label"
                    title="Marca esta cámara si la bocina del sitio está montada en su mismo poste"
                  >
                    <input
                      type="checkbox"
                      checked={config.sirenDeviceId != null}
                      onChange={(e) =>
                        setConfig({
                          ...config,
                          sirenDeviceId: e.target.checked ? siteSirens![0].id : null,
                        })
                      }
                    />
                    <span>Tiene bocina en el poste</span>
                  </label>
                )}
              </div>

              <div className="config-form-field">
                <label className="config-form-label">URL RTSP Original</label>
                <input
                  type="text"
                  value={config.urlRtspOriginal || ''}
                  onChange={(e) => setConfig({ ...config, urlRtspOriginal: e.target.value })}
                  className="config-input"
                  placeholder="rtsp://usuario:contraseña@ip:554/stream"
                />
              </div>

              <div className="config-form-field">
                <label className="config-form-label">Servidor de Grabaciones</label>
                <input
                  type="text"
                  value={config.recordingServerUrl || ''}
                  onChange={(e) => setConfig({ ...config, recordingServerUrl: e.target.value })}
                  className="config-input"
                  placeholder="http://192.168.1.100:8080"
                />
              </div>
            </div>
          )}

          {/* XTAM Video */}
          {deviceType === 'xtamVideo' && (
            <div className="device-config-form">
              <div className="config-form-field">
                <label className="config-form-label">Dirección IP</label>
                <input
                  type="text"
                  value={config.ipAddress || ''}
                  onChange={(e) => setConfig({ ...config, ipAddress: e.target.value })}
                  className="config-input"
                  placeholder="192.168.1.100"
                />
              </div>
            </div>
          )}

          {/* MinIoT */}
          {deviceType === 'miniot' && (
            <div className="device-config-form">
              <p className="config-field-description">
                El dispositivo MinIoT no requiere configuración adicional en este momento.
                Se asignará automáticamente al sitio.
              </p>
            </div>
          )}

          {/* Router */}
          {deviceType === 'router' && (
            <div className="device-config-form">
              <div className="config-form-field">
                <label className="config-form-label">Dirección IP</label>
                <input
                  type="text"
                  value={config.ipAddress || ''}
                  onChange={(e) => setConfig({ ...config, ipAddress: e.target.value })}
                  className="config-input"
                  placeholder="192.168.1.1"
                />
              </div>
            </div>
          )}

          {/* Botón de Pánico */}
          {deviceType === 'button' && (
            <div className="device-config-form">
              <div className="config-form-field">
                <label className="config-form-label">Identificación del Botón</label>
                <input
                  type="text"
                  value={config.identificationButton || ''}
                  onChange={(e) => setConfig({ ...config, identificationButton: e.target.value })}
                  className="config-input"
                  placeholder="BTN-001"
                />
              </div>

              <div className="config-form-field">
                <label className="config-form-label">Email de Registro</label>
                <input
                  type="email"
                  value={config.emailRegister || ''}
                  onChange={(e) => setConfig({ ...config, emailRegister: e.target.value })}
                  className="config-input"
                  placeholder="usuario@ejemplo.com"
                />
              </div>

              <div className="config-form-row">
                <div className="config-form-field">
                  <label className="config-form-label">Teléfono de Registro</label>
                  <input
                    type="number"
                    value={config.cellphoneRegister || ''}
                    onChange={(e) => setConfig({ ...config, cellphoneRegister: parseInt(e.target.value) || undefined })}
                    className="config-input"
                    placeholder="3001234567"
                  />
                </div>

                <div className="config-form-field">
                  <label className="config-form-label">ID Suscriptor</label>
                  <input
                    type="number"
                    value={config.idSubscriber || ''}
                    onChange={(e) => setConfig({ ...config, idSubscriber: parseInt(e.target.value) || undefined })}
                    className="config-input"
                    placeholder="1"
                  />
                </div>
              </div>

              <div className="config-form-field">
                <label className="config-form-label">Rol</label>
                <input
                  type="number"
                  value={config.role || ''}
                  onChange={(e) => setConfig({ ...config, role: parseInt(e.target.value) || undefined })}
                  className="config-input"
                  placeholder="1"
                />
              </div>

              <div className="config-form-checkbox">
                <label className="config-checkbox-label">
                  <input
                    type="checkbox"
                    checked={config.dataAuthorization || false}
                    onChange={(e) => setConfig({ ...config, dataAuthorization: e.target.checked })}
                  />
                  <span>Autorización de datos personales</span>
                </label>
              </div>
            </div>
          )}

          {/* Sirena */}
          {deviceType === 'siren' && (
            <div className="device-config-form">
              <div className="config-form-field">
                <label className="config-form-label">Dirección IP</label>
                <input
                  type="text"
                  value={config.ipAddress || ''}
                  onChange={(e) => setConfig({ ...config, ipAddress: e.target.value })}
                  className="config-input"
                  placeholder="192.168.1.50"
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="device-config-footer">
          {onCancel && (
            <button
              onClick={handleCancel}
              className="config-btn-cancel"
              style={{ marginRight: 'auto' }}
            >
              <X size={18} />
              Cancelar y Deseleccionar
            </button>
          )}
          {!isRequired && !onCancel && (
            <button onClick={onClose} className="config-btn-secondary">
              Cancelar
            </button>
          )}
          <button onClick={handleSave} className="config-btn-primary">
            <CheckCircle size={18} />
            Guardar configuración
          </button>
        </div>
      </div>

      {deviceType === 'camera' && (() => {
        const siteLat = siteLatitude != null && String(siteLatitude).trim() !== '' ? parseFloat(String(siteLatitude)) : NaN;
        const siteLng = siteLongitude != null && String(siteLongitude).trim() !== '' ? parseFloat(String(siteLongitude)) : NaN;
        const configLat = config.latitude != null && config.latitude !== '' ? parseFloat(config.latitude) : NaN;
        const configLng = config.longitude != null && config.longitude !== '' ? parseFloat(config.longitude) : NaN;
        return (
          <LocationPickerModal
            isOpen={showLocationPicker}
            onClose={() => setShowLocationPicker(false)}
            onSelectLocation={(lat, lng) => {
              setConfig(prev => ({ ...prev, latitude: lat.toFixed(6), longitude: lng.toFixed(6) }));
              setShowLocationPicker(false);
            }}
            initialLat={!isNaN(siteLat) ? siteLat : !isNaN(configLat) ? configLat : undefined}
            initialLng={!isNaN(siteLng) ? siteLng : !isNaN(configLng) ? configLng : undefined}
          />
        );
      })()}
    </div>
  );
};

export default DeviceConfigModal;

